import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { AccountDetailService } from '@rci/self-serve-fido/state/account/account-detail.service';
import { Subnav, SubnavCategory } from '@rogers/cdk/assembled/subnav';
import {
  AccountListFacadeService,
  CABLE_INTERNET_ACCOUNT_TYPE_NUMBER,
  WIRELESS_OR_CONSOLIDATE_ACCOUNT_TYPE_NUMBER,
} from '@rogers/self-serve-common/account';
import { BillingDetailService } from '@rci/self-serve-fido/state/billing-detail/billing-detail.service';
import { TranslateService } from '@ngx-translate/core';
import { Link } from '@rogers/cdk/link';

const billingHeaderCopyKey = 'subnav.billing.header';
const viewBillCopyKey = 'subnav.billing.sub-menu.view-bill';
const makePaymentCopyKey = 'subnav.billing.sub-menu.make-payment';
const promiseToPayCopyKey = 'subnav.billing.sub-menu.promise-to-pay';
const changePaymentMethodCopyKey = 'subnav.billing.sub-menu.change-payment-method';
const automaticPaymentCopyKey = 'subnav.billing.sub-menu.automatic-payment';
const historyCopyKey = 'subnav.billing.sub-menu.history';
const reportPaymentCopyKey = 'subnav.billing.sub-menu.report-payment';

const BILLING_SUBNAV_TRANSLATION_KEYS = [
  billingHeaderCopyKey,
  viewBillCopyKey,
  makePaymentCopyKey,
  promiseToPayCopyKey,
  changePaymentMethodCopyKey,
  automaticPaymentCopyKey,
  historyCopyKey,
  reportPaymentCopyKey,
];

const profileCopyKey = 'subnav.profile-settings';

const PROFILE_SUBNAV_TRANSLATION_KEYS = [
  profileCopyKey,
  'subnav.profile-submenu.overview',
  'subnav.profile-submenu.overview-alt',
  'subnav.profile-submenu.contact-billing',
  'subnav.profile-submenu.contact-billing-alt',
  'subnav.profile-submenu.profile-security',
  'subnav.profile-submenu.profile-security-alt',
  'subnav.profile-submenu.linked-account',
  'subnav.profile-submenu.linked-account-alt',
];

const overviewCopyKey = 'subnav.overview';

const ALLOWED_ACCOUNT_TYPES = [WIRELESS_OR_CONSOLIDATE_ACCOUNT_TYPE_NUMBER, CABLE_INTERNET_ACCOUNT_TYPE_NUMBER];

const SUBNAV_ROUTES_REGEX_CONFIG = [
  '(/overview)',
  '(/payments)',
  '(/profile-settings)',
  '(/promise-to-pay)',
  '(/report-payment)',
  '(/view-bill)',
  '(/link-account)',
  '(/orders)',
  '(/offers)',
];

export const ROUTES_REGEX = new RegExp(SUBNAV_ROUTES_REGEX_CONFIG.join('|'));

@Injectable({
  providedIn: 'root',
})
export class SecondaryNavService {
  constructor(
    private readonly router: Router,
    private readonly accountDetailService: AccountDetailService,
    private readonly accountListFacadeService: AccountListFacadeService,
    private readonly billingDetailService: BillingDetailService,
    private readonly translateService: TranslateService,
  ) {}

  getSecondaryNavData$(): Observable<Subnav> {
    const routerEvents$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => event as NavigationEnd),
      map(event => event.urlAfterRedirects || event.url),
      filter(url => ROUTES_REGEX.test(url)),
    );

    return routerEvents$.pipe(switchMap(url => this.getNavMenu$(url)));
  }

  private getNavMenu$(url: string): Observable<Subnav> {
    const navTitle$ = this.getNavTitle$(url);
    const overviewSubNav$ = this.getOverviewSubNav$(url);
    const billingSubNav$ = this.getBillingSubNav$(url);
    const offersSubNav$ = this.getOffersSubNav$(url);
    const settingsSubNav$ = this.getSettingsSubNav$(url);
    return combineLatest([navTitle$, overviewSubNav$, billingSubNav$, offersSubNav$, settingsSubNav$]).pipe(
      map(([navTitle, overviewSubNav, billingSubNav, offersSubNav, settingsSubNav]) => ({
        a11yLabel: '',
        categories: [overviewSubNav, billingSubNav, offersSubNav, settingsSubNav].filter(x => !!x),
        dropdownWidth: 'column' as 'full-width' | 'column',
        lob: navTitle,
        zIndex: 1,
      })),
    );
  }

  private getNavTitle$(url: string): Observable<string> {
    const sectionName = url.split('/')[1]?.split('?')[0] || '';
    let title: string;
    if (this.isBillingAndPaymentPage(sectionName)) {
      title = billingHeaderCopyKey;
    } else if (this.isProfileAndSettingPage(sectionName)) {
      title = profileCopyKey;
    } else if (this.isOffersPage(sectionName)) {
      title = 'subnav.offers';
    } else {
      title = overviewCopyKey;
    }
    return this.translateService.stream(title);
  }

  private getOverviewSubNav$(currentUrl: string): Observable<SubnavCategory> {
    return this.translateService.stream([overviewCopyKey, 'subnav.orders']).pipe(
      map(copy => {
        const overviewCopy = copy[overviewCopyKey];
        const link: Link = {
          copy: overviewCopy,
          val: 'overview',
          selected: this.isOverviewSubNavSelected(currentUrl),
        };

        return {
          link,
          sections: [
            {
              links: [
                {
                  copy: overviewCopy,
                  val: 'overview',
                },
                {
                  copy: copy['subnav.orders'],
                  val: 'orders',
                },
              ],
            },
          ],
        };
      }),
    );
  }

  private getBillingSubNav$(currentUrl: string): Observable<SubnavCategory> {
    return combineLatest([
      this.billingDetailService.getBillingDetail$(),
      this.translateService.stream(BILLING_SUBNAV_TRANSLATION_KEYS),
    ]).pipe(
      map(([billingDetail, copy]) => ({
        link: {
          copy: copy[billingHeaderCopyKey],
          val: '',
          selected: this.isBillingAndPaymentPage(currentUrl),
        },
        sections: [
          {
            links: [
              {
                copy: copy[viewBillCopyKey],
                val: 'view-bill',
                alt: copy[viewBillCopyKey],
              },
              {
                copy: copy[makePaymentCopyKey],
                val: 'payments/pay',
                alt: copy[makePaymentCopyKey],
              },
              {
                copy: copy[promiseToPayCopyKey],
                val: 'promise-to-pay',
                alt: copy[promiseToPayCopyKey],
              },
              billingDetail?.paymentMethod?.mopType !== 'R'
                ? {
                    copy: copy[changePaymentMethodCopyKey],
                    val: 'payments/auto',
                    alt: copy[changePaymentMethodCopyKey],
                  }
                : {
                    copy: copy[automaticPaymentCopyKey],
                    val: 'payments/auto',
                    alt: copy[automaticPaymentCopyKey],
                  },
              {
                copy: copy[historyCopyKey],
                val: 'payments/history',
                alt: copy[historyCopyKey],
              },
              {
                copy: copy[reportPaymentCopyKey],
                val: 'report-payment',
                alt: copy[reportPaymentCopyKey],
              },
            ].filter(x => !!x),
          },
        ],
      })),
    );
  }

  private getOffersSubNav$(currentUrl: string) {
    return combineLatest([this.canShowSubNav$(), this.translateService.stream('subnav.offers')]).pipe(
      map(([canShowSubNav, copy]) => {
        return canShowSubNav
          ? {
              link: {
                copy,
                val: 'offers',
                selected: this.isOffersPage(currentUrl),
              },
            }
          : null;
      }),
    );
  }

  private getSettingsSubNav$(currentUrl: string): Observable<SubnavCategory> {
    const profileAndSettings = 'profile-settings';
    return combineLatest([
      this.accountDetailService.selectedAccount$,
      this.translateService.stream(PROFILE_SUBNAV_TRANSLATION_KEYS),
    ]).pipe(
      map(([selectedAccount, copy]) => ({
        link: {
          copy: copy[profileCopyKey],
          val: profileAndSettings,
          selected: this.isProfileAndSettingPage(currentUrl),
        },
        sections: [
          {
            links: [
              {
                copy: copy['subnav.profile-submenu.overview'],
                val: profileAndSettings,
                alt: copy['subnav.profile-submenu.overview-alt'],
              },
              selectedAccount.isSubscriber
                ? null
                : {
                    copy: copy['subnav.profile-submenu.contact-billing'],
                    val: 'profile-settings/contact-billing',
                    alt: copy['subnav.profile-submenu.contact-billing-alt'],
                  },
              {
                copy: copy['subnav.profile-submenu.profile-security'],
                val: 'profile-settings/profile-security',
                alt: copy['subnav.profile-submenu.profile-security-alt'],
              },
              {
                copy: copy['subnav.profile-submenu.linked-account'],
                val: 'profile-settings/linked-accounts',
                alt: copy['subnav.profile-submenu.linked-account-alt'],
              },
            ].filter(x => !!x),
          },
        ],
      })),
    );
  }

  private isBillingAndPaymentPage(url: string) {
    return /payments|promise-to-pay|report-payment|view-bill/.test(url);
  }

  private isOffersPage(url: string) {
    return url.indexOf('offers') > -1;
  }

  private isProfileAndSettingPage(url: string) {
    return url.indexOf('profile-settings') > -1;
  }

  private canShowSubNav$(): Observable<boolean> {
    return this.accountListFacadeService.accountList$.pipe(
      map(accountList =>
        accountList.some(
          account => account.isActiveAccount && ALLOWED_ACCOUNT_TYPES.includes(account.accountTypeNumber),
        ),
      ),
    );
  }

  private isOverviewSubNavSelected(currentUrl: string) {
    return currentUrl.startsWith('/overview') || currentUrl.startsWith('/orders');
  }
}
